import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  Breadcrumbs,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import {
  Folder as FolderIcon,
  MoreHoriz as MoreHorizIcon
} from '@material-ui/icons';

import { useTimeout } from '../timeout.hook';
import { Breadcrumb } from './Breadcrumb.interface';
import { BreadcrumbContext } from './Breadcrumb.context';

const StyledBreadcrumbs = styled(Breadcrumbs)`
  color: #a1c1e2;
  & > *,
  li > * {
    font-weight: 500;
  }
  a {
    color: #a1c1e2;
    text-decoration: none;
    transition: color 100ms cubic-bezier(0.075, 0.82, 0.165, 1);
    &:hover {
      color: #fff;
    }
  }
  .MuiIconButton-root {
    padding: 0;
  }
`;

// NOTE: taken from https://stackoverflow.com/a/7557433/7013346
function isElementInViewport(element: Element): boolean {
  const rect: DOMRect = element.getBoundingClientRect();
  return (
    // rect.width <= 480 &&
    rect.height <= 30 && // new line
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export interface BreadcrumbSubheadingProps {
  root?: Breadcrumb;
  defaultData?: Breadcrumb;
}

export const BreadcrumbSubheading: React.FC<BreadcrumbSubheadingProps> = (
  props: BreadcrumbSubheadingProps
) => {
  const { root, defaultData } = props;
  const history = useHistory();
  const breadcrumbsRef = useRef<HTMLElement | null>(null);
  const { breadcrumbs } = useContext(BreadcrumbContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shouldCollapse, setShouldCollapse] = useState<boolean>(false);
  const [debounceUpdate, clearDebouncedUpdate] = useTimeout();

  const items: Breadcrumb[] = useMemo(() => {
    const all: Breadcrumb[] =
      breadcrumbs.length === 0 && defaultData ? [defaultData] : breadcrumbs;
    return root ? [root, ...all] : all;
  }, [root, breadcrumbs, defaultData]);

  const startIndex: number = 1;
  const endIndex: number = items.length - 1;
  // should have more than 2 items to collapse
  const collapsed: boolean = shouldCollapse && items.length > 2;

  const first: Breadcrumb[] = useMemo(() => {
    return collapsed ? items.slice(0, startIndex) : items;
  }, [items, collapsed, startIndex]);

  const between: Breadcrumb[] = useMemo(() => {
    return collapsed ? items.slice(startIndex, endIndex) : [];
  }, [items, collapsed, startIndex, endIndex]);

  const last: Breadcrumb[] = useMemo(() => {
    return collapsed ? items.slice(endIndex, items.length) : [];
  }, [items, collapsed, endIndex]);

  const updateCollapse = useCallback((ms: number = 100) => {
    debounceUpdate(() => {
      if (breadcrumbsRef.current) {
        setShouldCollapse(!isElementInViewport(breadcrumbsRef.current));
      }
    }, ms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const update = () => updateCollapse();
    window.addEventListener('resize', update);
    update();
    return () => {
      window.removeEventListener('resize', update);
      clearDebouncedUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbs, updateCollapse]);

  useLayoutEffect(() => {
    // check again if not collapsed
    !collapsed && updateCollapse(0);
  }, [collapsed, updateCollapse]);

  const renderBreadcrumb = (breadcrumb: Breadcrumb) => (
    <Typography
      component={Link}
      variant='body1'
      to={breadcrumb.path || ''}
      key={`${breadcrumb.label}::${breadcrumb.path}`}
    >
      {breadcrumb.label}
    </Typography>
  );

  return (
    <StyledBreadcrumbs ref={breadcrumbsRef} aria-label='breadcrumb'>
      {first.map(renderBreadcrumb)}
      {between.length > 0 && (
        <span>
          <IconButton
            size='small'
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreHorizIcon fontSize='small' htmlColor='#a1c1e2' />
          </IconButton>
          <Menu
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
          >
            {between.map((breadcrumb: Breadcrumb) => (
              <MenuItem
                key={`${breadcrumb.label}::${breadcrumb.path}`}
                onClick={() => breadcrumb.path && history.push(breadcrumb.path)}
              >
                <ListItemIcon>
                  <FolderIcon fontSize='small' />
                </ListItemIcon>
                <Typography variant='inherit'>{breadcrumb.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </span>
      )}
      {last.map(renderBreadcrumb)}
    </StyledBreadcrumbs>
  );
};

export default BreadcrumbSubheading;
